import React, { useEffect, useState } from "react";
import { Sparkle, X } from "@phosphor-icons/react";
import { useT } from "../lib/i18n";
import CoachTab from "./tabs/CoachTab";

export default function AssistantLauncher({ project, version }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  function toggle() {
    setHasOpened(true);
    setOpen((current) => !current);
  }

  useEffect(() => {
    if (!open) return undefined;
    function closeOnEscape(event) {
      if (event.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  return (
    <>
      {hasOpened && (
        <aside
          id="assistant-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="assistant-panel-title"
          aria-hidden={!open}
          className={`assistant-panel grid-card fixed bottom-20 right-4 z-50 flex w-[min(94vw,680px)] flex-col overflow-hidden shadow-2xl ${open ? "" : "hidden"}`}
          style={{ maxHeight: "min(80vh, 820px)" }}
          data-testid="assistant-panel"
        >
          <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--border)] bg-[var(--surface-elevated)] px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <Sparkle size={18} weight="fill" className="shrink-0 text-cyan-500" />
              <div className="min-w-0">
                <h2 id="assistant-panel-title" className="text-sm font-bold text-[var(--text)]">{t("assistant.title")}</h2>
                {project?.name && <p className="truncate text-xs text-[var(--text-2)]">{project.name}</p>}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("assistant.close")}
              title={t("assistant.close")}
              data-testid="assistant-close"
              className="shrink-0 rounded-sm p-2 text-[var(--text-2)] transition-colors hover:bg-[var(--surface-hover)] hover:text-[var(--text)]"
            >
              <X size={16} />
            </button>
          </header>
          <div className="min-h-0 overflow-y-auto p-3 sm:p-4">
            {project ? (
              <CoachTab
                key={`${project.id}-${version?.version ?? "no-version"}`}
                project={project}
                version={version}
              />
            ) : (
              <p className="rounded-sm border border-[var(--border)] bg-[var(--surface)] p-5 text-sm leading-6 text-[var(--text-2)]">
                {t("assistant.project_required")}
              </p>
            )}
          </div>
        </aside>
      )}

      <button
        type="button"
        onClick={toggle}
        aria-label={open ? t("assistant.close") : t("assistant.launcher")}
        aria-expanded={open}
        aria-controls="assistant-panel"
        title={open ? t("assistant.close") : t("assistant.launcher")}
        data-testid="assistant-launcher"
        className="fixed bottom-4 right-4 z-[51] inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-600 px-4 py-3 text-sm font-semibold text-white shadow-xl transition-colors hover:bg-cyan-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-300"
      >
        <Sparkle size={18} weight="fill" />
        <span>{t("assistant.launcher")}</span>
      </button>
    </>
  );
}
